import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import { useToast } from '../components/Toast';
import { getEvents, registerEvent } from '../api/client';

const TYPE_COLORS = { webinar: '#0A84FF', seminar: '#FFCC00', meetup: '#30D158', news: '#FF9F0A' };
// Цвет текста на залитых кнопках: на жёлтом — тёмный, на остальных — белый
const onTypeColor = (type) => type === 'seminar' ? '#0A0A0A' : 'white';
const TYPE_LABELS = { webinar: 'Вебинар', seminar: 'Семинар', meetup: 'Встреча', news: 'Новость' };

const TypeIcon = ({ type, size = 20, color }) => {
  const c = color || TYPE_COLORS[type] || '#636366';
  const icons = {
    webinar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
    seminar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5"/></svg>,
    meetup: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
    news: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  };
  return icons[type] || null;
};
const FILTERS = ['Все', 'Вебинары', 'Семинары', 'Встречи', 'Новости'];
const FILTER_MAP = { 'Вебинары': 'webinar', 'Семинары': 'seminar', 'Встречи': 'meetup', 'Новости': 'news' };

function googleCalendarUrl(ev) {
  const pad = (n) => String(n).padStart(2, '0');
  const toGCal = (d) => {
    const dt = new Date(d);
    return dt.getFullYear() + pad(dt.getMonth() + 1) + pad(dt.getDate()) + 'T' +
      pad(dt.getHours()) + pad(dt.getMinutes()) + '00';
  };

  const start = toGCal(ev.date);
  const end = ev.end_date ? toGCal(ev.end_date) : toGCal(new Date(new Date(ev.date).getTime() + 90 * 60 * 1000));
  const timeStr = new Date(ev.date).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
  const desc = (ev.description || '') + '\n\nНачало: ' + timeStr + ' (Chisinau)' + (ev.link ? '\nСсылка: ' + ev.link : '');
  const location = ev.link || ev.location || '';

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title,
    dates: start + '/' + end,
    details: desc,
    location: location,
    ctz: 'Europe/Chisinau',
  });

  return 'https://calendar.google.com/calendar/render?' + params.toString();
}

function openLink(url) {
  if (window.Telegram?.WebApp?.openLink) {
    window.Telegram.WebApp.openLink(url);
  } else {
    window.open(url, '_blank');
  }
}

function contactUrl(raw) {
  if (!raw) return null;
  const s = raw.trim();
  if (s.startsWith('http')) return s;
  if (s.startsWith('@')) return 'https://t.me/' + s.slice(1);
  if (s.startsWith('+') || /^\d/.test(s)) return 'https://wa.me/' + s.replace(/[^0-9]/g, '');
  return s;
}

function isRecentlyEnded(ev) {
  if (!ev.end_date && !ev.date) return false;
  const end = new Date(ev.end_date || ev.date).getTime();
  const hoursAfterEnd = (Date.now() - end) / (1000 * 60 * 60);
  return hoursAfterEnd >= 2 && hoursAfterEnd < 26;
}

export default function EventsPage({ onNavigate }) {
  const { toast, toastError } = useToast();
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('Все');
  const [showPast, setShowPast] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    const type = FILTER_MAP[filter];
    const params = type ? `type=${type}` : '';
    getEvents(params).then(data => {
      setEvents(data.events || []);
      setCounts(data.counts || {});
    }).catch(console.error);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Refresh when app becomes visible (e.g. returning from admin panel)
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const handleReminder = (ev) => {
    // Must be synchronous from click — Telegram blocks async openLink
    registerEvent(ev.id).then(() => { load(); toast('Вы записаны'); }).catch(() => toastError('Не удалось записаться'));
    openLink(googleCalendarUrl(ev));
  };

  const handleRegister = (ev) => {
    registerEvent(ev.id).then(() => { load(); toast('Вы записаны'); }).catch(() => toastError('Не удалось записаться'));
  };

  const st = (ev) => ev.effective_status || ev.status;
  const upcoming = events.filter(e => st(e) !== 'past');
  const past = events.filter(e => st(e) === 'past');

  const formatDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    const days = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'];
    return {
      day: dt.getDate(),
      month: dt.toLocaleString('ru', { month: 'short' }).toUpperCase(),
      weekday: days[dt.getDay()],
      time: dt.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const isOnline = (type) => type === 'webinar' || type === 'seminar';

  const renderActionButtons = (ev) => {
    const color = TYPE_COLORS[ev.type];
    const evSt = st(ev);
    const isLive = evSt === 'live';

    const mainBtnStyle = {
      flex: 1, border: 'none', borderRadius: 10, padding: 10,
      fontSize: 13, fontWeight: 700, cursor: 'pointer',
    };

    // Meetup/seminar with external link — direct open (офлайн-события с внешней регистрацией)
    if ((ev.type === 'meetup' || ev.type === 'seminar') && ev.link) {
      return (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => openLink(ev.link)} style={{
            ...mainBtnStyle, background: color, color: onTypeColor(ev.type),
          }}>Регистрация</button>
          <button onClick={() => setExpandedId(expandedId === ev.id ? null : ev.id)} style={{
            background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
            border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Подробнее</button>
        </div>
      );
    }

    let mainBtn;
    if (isOnline(ev.type) && ev.date) {
      // Synchronous openLink from click handler — Telegram won't block it
      mainBtn = (
        <button onClick={() => handleReminder(ev)} style={{
          ...mainBtnStyle, background: color, color: onTypeColor(ev.type),
        }}>Поставить напоминание</button>
      );
    } else {
      mainBtn = (
        <button onClick={() => handleRegister(ev)} style={{
          ...mainBtnStyle, background: color, color: onTypeColor(ev.type),
        }}>Записаться</button>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* LIVE: extra button to join stream */}
        {isLive && isOnline(ev.type) && ev.link && (
          <button onClick={() => openLink(ev.link)} style={{
            ...mainBtnStyle, background: '#FF3B30', color: 'white',
            animation: 'pulse 2s ease-in-out infinite',
          }}>Подключиться к онлайн</button>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          {mainBtn}
          <button onClick={() => setExpandedId(expandedId === ev.id ? null : ev.id)} style={{
            background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
            border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Подробнее</button>
        </div>
      </div>
    );
  };

  const renderExpanded = (ev) => {
    if (expandedId !== ev.id) return null;
    const color = TYPE_COLORS[ev.type];
    const contact = contactUrl(ev.speaker_contact);

    return (
      <div style={{
        padding: '0 14px 14px', borderTop: '1px solid var(--border)',
        animation: 'fadeInUp 0.2s ease both',
      }}>
        {/* Full description */}
        {ev.description && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 12 }}>
            {ev.description}
          </div>
        )}

        {/* Speaker info */}
        {ev.speaker && (
          <div style={{
            marginTop: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 12,
            border: '1px solid var(--border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 24, flexShrink: 0,
                background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {ev.speaker_photo ? (
                  <img src={ev.speaker_photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: 18, fontWeight: 700, color: color }}>
                    {ev.speaker.split(' ').map(w => w[0]).join('').slice(0, 2)}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{ev.speaker}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>Спикер</div>
              </div>
            </div>
            {ev.speaker_bio && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 10 }}>
                {ev.speaker_bio}
              </div>
            )}
          </div>
        )}

        {/* Contact expert button */}
        {contact && (
          <button onClick={() => openLink(contact)} style={{
            width: '100%', marginTop: 10,
            background: '#25D366', color: 'white', border: 'none',
            borderRadius: 10, padding: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>Связаться с экспертом</button>
        )}
      </div>
    );
  };

  const renderPastEvent = (ev) => {
    const recentlyEnded = isRecentlyEnded(ev);
    const hasRecording = !!ev.recording_url;
    return (
      <div key={ev.id} style={{
        background: 'var(--bg-card)', borderRadius: 14, overflow: 'hidden',
        border: '1px solid var(--border)', opacity: hasRecording ? 0.95 : recentlyEnded ? 0.8 : 0.5,
        display: 'flex',
      }}>
        <div style={{
          width: 60, minHeight: 60, flexShrink: 0,
          background: hasRecording ? 'rgba(10,132,255,0.1)' : 'rgba(99,99,102,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', position: 'relative',
        }}>
          {ev.speaker_photo ? (
            <img src={ev.speaker_photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0, filter: hasRecording ? 'none' : 'grayscale(0.5)' }} />
          ) : (
            <div style={{ opacity: hasRecording ? 0.8 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {hasRecording ? <svg width="20" height="20" viewBox="0 0 24 24" fill="#0A84FF"><polygon points="5 3 19 12 5 21 5 3"/></svg> : <TypeIcon type={ev.type} size={20} />}
            </div>
          )}
        </div>
        <div style={{ flex: 1, padding: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(99,99,102,0.12)', color: 'var(--text-tertiary)' }}>
            {TYPE_LABELS[ev.type]}
          </span>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, color: hasRecording ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{ev.title}</div>
          {hasRecording ? (
            <div onClick={() => openLink(ev.recording_url)} style={{
              fontSize: 12, color: '#0A84FF', marginTop: 6, cursor: 'pointer', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              Смотреть запись
            </div>
          ) : recentlyEnded && isOnline(ev.type) ? (
            <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 6 }}>
              Запись скоро будет готова
            </div>
          ) : isOnline(ev.type) ? (
            <div onClick={() => onNavigate && onNavigate('library')} style={{
              fontSize: 11, color: '#0A84FF', marginTop: 6, cursor: 'pointer',
            }}>
              Запись в Библиотеке
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div>
      <Header title="События" />
      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', animation: 'fadeInUp 0.3s ease both' }}>
          {FILTERS.map(f => {
            const type = FILTER_MAP[f];
            const count = f === 'Все' ? (counts.total || 0) : (counts.by_type?.find(c => c.type === type)?.count || 0);
            return (
              <button key={f} onClick={() => setFilter(f)} style={{
                background: filter === f ? 'var(--accent)' : 'var(--bg-card)',
                color: filter === f ? '#0D0D0D' : 'var(--text-secondary)',
                border: '1px solid ' + (filter === f ? 'var(--accent)' : 'var(--border)'),
                borderRadius: 20, padding: '6px 12px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              }}>{f} {count > 0 ? `(${count})` : ''}</button>
            );
          })}
        </div>

        {upcoming.length > 0 && (
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', marginTop: 4 }}>
            Предстоящие ({upcoming.length})
          </div>
        )}

        {upcoming.map((ev, i) => {
          const d = formatDate(ev.date);
          const color = TYPE_COLORS[ev.type];
          const evSt = st(ev);

          if (ev.type === 'news') {
            return (
              <div key={ev.id} style={{
                background: 'var(--bg-card)', borderRadius: 16, padding: 16,
                border: '1px solid var(--border)',
                animation: `fadeInUp 0.3s ease ${0.05 * i}s both`,
              }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: 'rgba(255,159,10,0.12)', color: '#FF9F0A', display: 'inline-flex', alignItems: 'center', gap: 4 }}><TypeIcon type="news" size={12} color="#FF9F0A" /> НОВОСТЬ</span>
                <div style={{ fontSize: 15, fontWeight: 600, marginTop: 8, lineHeight: 1.3 }}>{ev.title}</div>
                {ev.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ev.description}</div>}
              </div>
            );
          }

          return (
            <div key={ev.id} style={{
              background: 'var(--bg-card)', borderRadius: 20, overflow: 'hidden',
              border: evSt === 'live' ? '2px solid #FF3B30' : '1px solid var(--border)',
              animation: `fadeInUp 0.3s ease ${0.05 * i}s both`,
            }}>
              <div style={{ display: 'flex' }}>
                <div style={{
                  width: 130, minHeight: 150, flexShrink: 0, position: 'relative',
                  background: `linear-gradient(135deg, ${color}22, ${color}08)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {ev.speaker_photo ? (
                    <img src={ev.speaker_photo} alt={ev.speaker || ''} style={{
                      width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0,
                    }} />
                  ) : ev.speaker ? (
                    <div style={{ fontSize: 36, fontWeight: 700, color: color, opacity: 0.6 }}>
                      {ev.speaker.split(' ').map(w => w[0]).join('').slice(0, 2)}
                    </div>
                  ) : (
                    <TypeIcon type={ev.type} size={36} />
                  )}
                  {d && (
                    <div style={{
                      position: 'absolute', top: 8, left: 8,
                      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                      borderRadius: 8, padding: '4px 8px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1, color: '#fff' }}>{d.day}</div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: color, marginTop: 1 }}>{d.month}</div>
                    </div>
                  )}
                  {evSt === 'live' && (
                    <div style={{
                      position: 'absolute', bottom: 8, left: 8, right: 8,
                      background: '#FF3B30', borderRadius: 6, padding: '3px 0',
                      textAlign: 'center', fontSize: 10, fontWeight: 800, color: '#fff',
                      animation: 'pulse 2s ease-in-out infinite',
                    }}>LIVE</div>
                  )}
                </div>

                <div style={{ flex: 1, padding: '14px 14px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: color + '22', color: color }}>
                        {TYPE_LABELS[ev.type]}
                      </span>
                      {evSt === 'live' && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'rgba(255,69,58,0.15)', color: '#FF3B30' }}>
                          LIVE
                        </span>
                      )}
                      {ev.price > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'rgba(255,204,0,0.12)', color: 'var(--accent)' }}>
                          {ev.price} MDL
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>
                      {ev.title}
                    </div>

                    {ev.speaker && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                        {ev.speaker}
                      </div>
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>
                      {d && <span>{d.weekday}, {d.time}</span>}
                      <span>{ev.location || 'Online'}</span>
                      {ev.registrations_count > 0 && <span>{ev.registrations_count} чел.</span>}
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    {renderActionButtons(ev)}
                  </div>
                </div>
              </div>

              {/* Expanded details */}
              {renderExpanded(ev)}
            </div>
          );
        })}

        {past.length > 0 && (
          <>
            <button onClick={() => setShowPast(!showPast)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: 'none', border: '1px solid var(--border)', borderRadius: 12,
              padding: '10px 16px', cursor: 'pointer', marginTop: 8,
              color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 600,
            }}>
              {showPast ? '▲ Скрыть прошедшие' : `▼ Прошедшие (${past.length})`}
            </button>
            {showPast && past.map(ev => renderPastEvent(ev))}
          </>
        )}

        {events.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#48484A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <div>Нет событий</div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
